import * as React from 'react';
import {
  Alert,
  Button,
  EmptyState,
  Inline,
  ListBand,
  ListColumn,
  Loading,
  Stack,
  Text,
  type ListBandProps,
} from '@relay-towing/ui';
import type { PagedList } from '../../hooks/usePagedList';

export interface RowGroups<Item> {
  /**
   * The group a row is in: the field the server grouped by. Undefined for a
   * row that does not carry it — a member who only drives is not sent an
   * impound's status — and that row's band is found from the server's order
   * instead: the rows come in group order, and `meta.groups` lists every
   * group with its count, so the first so many rows are the first group's.
   */
  of: (item: Item) => string | undefined;
  /** A group's heading: "Sent". */
  label: (key: string) => string;
  /**
   * The band's tint: the badge tone of what the group is, so a red band
   * sits where red badges would have been.
   */
  tone?: (key: string) => ListBandProps['tone'];
}

/** A band's heading: its name, and how many rows it has on every page. */
function BandHeading({ label, count }: { label: string; count: number }) {
  return (
    <Inline justify="between" align="center">
      <Text variant="overline" tone="muted" as="h2">
        {label}
      </Text>
      <Text variant="caption" tone="muted" numeric>
        {count}
      </Text>
    </Inline>
  );
}

function Rows<Item>({
  items,
  children,
}: {
  items: Item[];
  children: (item: Item) => React.ReactNode;
}) {
  // No card around the rows: the Figma's lists are ruled off row from row
  // on the page itself.
  return (
    <Stack gap={0} divided>
      {items.map(children)}
    </Stack>
  );
}

/**
 * A server list's rows, with its loading, failed and empty states and a way
 * to the next page. Every list in the shell is one of these, so they all
 * fail and load the same way.
 *
 * With `title`, the rows are one band under that heading, for a screen
 * with more than one list on it.
 *
 * With `toolbar`, the list is the grouped column the dashboard draws beside
 * a record: the toolbar, then the screen's ListColumnRows, flush with its
 * edges. With `groups` as well, the list was asked for `groupBy`, so the
 * server has ordered the rows by group: a tinted ListBand starts where the
 * group changes, headed with the count from `meta.groups`, which counts
 * every page rather than the rows loaded so far. The app never groups rows
 * itself. `table` draws the toolbar's Table instead of the bands, a
 * `shape="band"` Table in the same column. Without `toolbar` the rows are
 * ruled, as the Figma's calls list draws them.
 */
export function PagedRows<Item>({
  list,
  empty,
  groups,
  title,
  toolbar,
  table,
  children,
}: {
  list: PagedList<Item>;
  /** What an empty list says: "No impounds at the lot". */
  empty: string;
  /** The server's groups, as bands; only with a `toolbar`. */
  groups?: RowGroups<Item>;
  /** Heads the whole list as one band, for a list that is one of several. */
  title?: string;
  /** The ListToolbar over a grouped column, kept while the list loads. */
  toolbar?: React.ReactNode;
  /** The rows as a table, when the toolbar is on Table. */
  table?: (items: Item[]) => React.ReactNode;
  children: (item: Item) => React.ReactNode;
}) {
  const state = list.isLoading ? (
    <Loading label="Loading" />
  ) : list.isError ? (
    <Alert
      tone="destructive"
      title="This list did not load"
      actions={
        <Button variant="outline" size="sm" onClick={list.refetch}>
          Try again
        </Button>
      }
    >
      Check the connection and try again.
    </Alert>
  ) : list.items.length === 0 ? (
    <EmptyState title={empty} size="sm" />
  ) : null;

  const order = list.meta?.groups ?? [];
  const keyAt = (index: number) => {
    let end = 0;
    for (const group of order) {
      end += group.count;
      if (index < end) return group.key;
    }
    return order.at(-1)?.key ?? '';
  };
  const bands: Array<{ key: string; items: Item[] }> = [];
  if (groups && !state) {
    list.items.forEach((item, index) => {
      const key = groups.of(item) ?? keyAt(index);
      const last = bands[bands.length - 1];
      if (last && last.key === key) last.items.push(item);
      else bands.push({ key, items: [item] });
    });
  }
  const counts = new Map(order.map((group) => [group.key, group.count]));
  const countOf = (band: { key: string; items: Item[] }) =>
    counts.get(band.key) ?? band.items.length;

  const more = list.hasMore ? (
    <Button
      variant="outline"
      onClick={list.loadMore}
      loading={list.isLoadingMore}
    >
      Show more
    </Button>
  ) : null;

  if (toolbar !== undefined) {
    // The toolbar stays while the list loads or comes back empty, so the
    // search box someone is typing in is not taken away from them.
    return (
      <>
        <ListColumn bleed>
          {toolbar}
          {state
            ? null
            : table
              ? table(list.items)
              : groups
                ? bands.map((band) => (
                    <Stack key={band.key} as="section" gap={0}>
                      <ListBand
                        label={groups.label(band.key)}
                        count={countOf(band)}
                        tone={groups.tone?.(band.key) ?? 'neutral'}
                      />
                      {band.items.map(children)}
                    </Stack>
                  ))
                : list.items.map(children)}
        </ListColumn>
        {state}
        {state ? null : more}
      </>
    );
  }

  if (state) return state;

  return (
    <Stack gap={4}>
      {title ? (
        <Stack gap={2} as="section">
          <BandHeading
            label={title}
            count={list.meta?.totalCount ?? list.items.length}
          />
          <Rows items={list.items}>{children}</Rows>
        </Stack>
      ) : (
        <Rows items={list.items}>{children}</Rows>
      )}
      {more}
    </Stack>
  );
}
